import Component from '@glimmer/component'
import { service } from '@ember/service'
import { task, timeout, type TaskInstance, all, didCancel } from 'ember-concurrency'
import type { IntlService } from 'ember-intl'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type SchoolClass from 'teacher-ui-client/models/school-class'
import { ThemeKeys } from 'teacher-ui-client/utils/themes'
import { on } from '@ember/modifier'
import { t } from 'ember-intl'
import UiButton from 'teacher-ui-client/components/ui-button'
import UiButtonState from 'teacher-ui-client/components/ui-button/state'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { isConflictError } from 'teacher-ui-client/resource-conflict/error'
import type ResourceConflictService from 'teacher-ui-client/services/resource-conflict'
import type { Log } from '@blakeelearning/log'
import joinList from 'teacher-ui-client/helpers/join-list'
import { tracked } from '@glimmer/tracking'
import { get } from '@ember/object'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { or } from 'ember-truth-helpers'
import { pick } from 'lodash'
import type Owner from '@ember/owner'
import { hasId } from 'teacher-ui-client/utils/type-guards'
import { InlineAlert, AlertType, InlineAlertSubtitle } from 'teacher-ui-client/components/inline-alert'

interface Signature {
  Element: HTMLDivElement
  Args: {
    schoolClasses: SchoolClass[]
    themeKey?: ThemeKeys
    closeModalAction: () => unknown
    onSuccess?: () => unknown
  }
}

interface Theme {
  alertHeaderBg: string
  headerBg: string
  rowBg: string
}

const themeClasses: Partial<Record<ThemeKeys, Theme>> = {
  [ThemeKeys.blue]: {
    alertHeaderBg: 'bg-oceany-blue-300',
    headerBg: 'bg-oceany-blue-100',
    rowBg: 'even:bg-oceany-blue-50 hover:bg-oceany-blue-100',
  },
  [ThemeKeys.green]: {
    alertHeaderBg: 'bg-ms-green-300',
    headerBg: 'bg-ms-green-100',
    rowBg: 'even:bg-ms-green-50 hover:bg-ms-green-100',
  },
  [ThemeKeys.darkBlue]: {
    alertHeaderBg: 'bg-wl-blue-250',
    headerBg: 'bg-wl-blue-100',
    rowBg: 'even:bg-wl-blue-50 hover:bg-wl-blue-100',
  },
  [ThemeKeys.default]: {
    alertHeaderBg: 'bg-dusty-black-300',
    headerBg: 'bg-dusty-black-100',
    rowBg: 'even:bg-dusty-black-50 hover:bg-dusty-black-100',
  },
}

type SchoolClassId = string

interface DeletableSchoolClass {
  viewModel: {
    id: string
    name: string
    studentCount: number
    teacherNames: string[]
  }
  record: SchoolClass & { id: string } // inaccessible after the record has been unloaded from the store
}

export class FormDeleteClasses extends Component<Signature> {
  @service intl!: IntlService

  @service flashQueue!: FlashQueueService

  @service resourceConflict!: ResourceConflictService

  @service log!: Log

  readonly schoolClasses: DeletableSchoolClass[]

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args)
    this.schoolClasses = args.schoolClasses.filter(hasId).map((schoolClass) => ({
      viewModel: pick(schoolClass, ['id', 'name', 'studentCount', 'teacherNames']),
      record: schoolClass,
    }))
  }

  get themeClasses() {
    return themeClasses[this.args.themeKey ?? ThemeKeys.default]
  }

  @tracked deletions: Record<SchoolClassId, TaskInstance<Promise<unknown>>> = {}

  deleteClasses = task({ drop: true }, async (classesToDelete: DeletableSchoolClass[]) => {
    try {
      // run a delete task for each, paired with id, so the UI can view the state of each deletion task by class id
      this.deletions = classesToDelete.reduce((acc, schoolClass) => {
        return { ...acc, [schoolClass.record.id]: this.deleteClass.perform(schoolClass.record) }
      }, {})
      // disable this eslint rule, as we are doing what it says (instead of Promise.all we're using e-c all() function)
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      await all(Object.values(this.deletions))
    } catch (error) {
      if (isConflictError(error)) {
        this.resourceConflict.handleError('delete classes', error)
      } else if (!didCancel(error)) {
        this.flashQueue.addFail({ subtitle: this.intl.t('manage.rollover.steps.delete_classes.deletePrompt.failed') })
        this.log.error('failed to delete class during rollover', error ?? '')
      }
    } finally {
      // just to let the finished / cancelled / errored UX display for a sec before (likely) closing the modal
      await timeout(1000)
      /* Wait for any in flight delete requests to settle (in case of cancel). If the class is huge the deletion request
       * can take a while to return, and we don't want them trying a new deletion while that one's happening in the bg
       */
      while (classesToDelete.some((schoolClass) => schoolClass.record.isSaving)) {
        await timeout(1000)
      }
      const taskInstances = Object.values(this.deletions)
      const successCount = taskInstances.filter((taskInstance) => taskInstance.isSuccessful).length
      const errorCount = taskInstances.filter((taskInstance) => taskInstance.isError).length
      if (successCount > 0) {
        this.flashQueue.addSuccess({
          subtitle: this.intl.t('manage.manageClasses.classes.bulkActions.deleteClasses.success', {
            count: successCount,
          }),
        })
      }
      if (successCount > 0 && errorCount === 0) {
        this.deletions = {}
        this.args.onSuccess?.()
      }
    }
  })

  deleteClass = task(
    { enqueue: true }, // queue up, execute one at a time, in order. Don't want to overload gravity.
    async (record: DeletableSchoolClass['record']) => {
      /**
       * Space the deletions apart slightly, so that if the prior deletion errored, there's time to cancel this task
       * before it begins deleting the next record
       */
      await timeout(100)

      return record.destroyRecord()
    },
  )

  onSubmit = (event: Event) => {
    event.preventDefault()

    void this.deleteClasses.perform(this.schoolClasses)
  }

  cancel = () => {
    if (this.deleteClasses.isRunning) {
      void this.deleteClasses.cancelAll()
    }
    this.args.closeModalAction()
  }

  <template>
    <div ...attributes>

      <div class="px-4 pb-4 {{this.themeClasses.alertHeaderBg}}">
        <InlineAlert @type={{AlertType.Critical}} role="alert">
          <InlineAlertSubtitle>
            {{t
              "manage.manageClasses.classes.bulkActions.deleteClasses.confirm"
              count=this.schoolClasses.length
              htmlSafe=true
            }}
          </InlineAlertSubtitle>
        </InlineAlert>
      </div>

      <form {{on "submit" this.onSubmit}}>
        <div class="max-h-80 overflow-y-auto">
          <table class="w-full table-fixed" tabindex="0">
            <thead>
              <tr class="text-dusty-black-300 divide-x divide-white font-bold">
                <th class="sticky top-0 w-1/12 px-4 py-3 {{this.themeClasses.headerBg}}"></th>
                <th class="sticky top-0 w-5/12 px-4 py-3 {{this.themeClasses.headerBg}}">
                  {{t "schoolClass.label"}}
                </th>
                <th class="sticky top-0 w-2/12 px-4 py-3 whitespace-nowrap {{this.themeClasses.headerBg}}">
                  {{t "manage.manageClasses.classes.studentCount"}}</th>
                <th class="sticky top-0 w-5/12 px-4 py-3 {{this.themeClasses.headerBg}}">
                  {{t "manage.manageClasses.classes.teacherNames"}}</th>
              </tr>
            </thead>
            <tbody>
              {{#each this.schoolClasses as |schoolClass index|}}
                {{#let (get this.deletions schoolClass.viewModel.id) as |taskInstance|}}
                  <tr data-test-row={{index}} class={{this.themeClasses.rowBg}}>
                    <td class="px-4 py-3">
                      {{#if taskInstance.isRunning}}
                        <PhosphorIcon @icon="phosphor-circle-notch-bold" class="animate-spin" />
                      {{else if taskInstance.isSuccessful}}
                        <PhosphorIcon
                          @icon="phosphor-check-bold"
                          class="text-forest-green-350"
                          data-test-success-icon
                        />
                      {{else if (or taskInstance.isError taskInstance.isCanceled)}}
                        <PhosphorIcon @icon="phosphor-x-bold" class="text-watermelony-red-350" />
                      {{/if}}
                    </td>
                    <td class="truncate px-4 py-3">{{schoolClass.viewModel.name}}</td>
                    <td class="px-4 py-3">{{schoolClass.viewModel.studentCount}}</td>
                    <td class="truncate px-4 py-3">{{joinList schoolClass.viewModel.teacherNames ", "}}</td>
                  </tr>
                {{/let}}
              {{/each}}
            </tbody>
          </table>
        </div>
        <div class="border-dusty-black-50 border-t p-2 text-right">
          <UiButton data-test-cancel type="reset" {{on "click" @closeModalAction}}>
            {{t "manage.manageClasses.classes.bulkActions.cancel"}}
          </UiButton>
          <UiButton
            type="submit"
            @appearance={{if this.deleteClasses.isRunning ButtonAppearance.muted ButtonAppearance.red}}
            @disabled={{this.deleteClasses.isRunning}}
          >
            <UiButtonState @isLoading={{this.deleteClasses.isRunning}}>
              {{t "manage.manageClasses.classes.bulkActions.deleteClasses.delete" count=@schoolClasses.length}}
            </UiButtonState>
          </UiButton>
        </div>
      </form>
    </div>
  </template>
}

export default FormDeleteClasses
